import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useUserContext } from "../../utils/UserContext";
import { API_URL } from "../../utils/config";
import { fetchAutenticat } from "../../utils/auth";
import { getRolsAssignables, tincPermis } from "../../utils/Permisos";
import TeclaEscEnter from "../../utils/Teclaescenter";
import AjudaMestres from "./AjudaMestres";
import { BotoAjuda } from "./AjudaModal";

function Detallsusuari({
  mode,
  usuari,
  usuaris,
  onSave,
  onCancel,
  onNavigate,
  onCanviarMode,
  onDeleted,
}) {
  const isReadOnly = mode === "consultar" || mode === "esborrar";
  TeclaEscEnter(onCancel, null);

  const { empresaSeleccionada, esSuperUsuari, usuariActiu, rolUsuari } = useUserContext();
  const esAutoEdicio =
    mode === "modificar" && !!usuari && usuariActiu != null && Number(usuari.idUsuaris) === Number(usuariActiu);

  const [nomUsuari, setNomUsuari] = useState("");
  const [password, setPassword] = useState("");
  const [passwordReset, setPasswordReset] = useState("");
  const [passwordActual, setPasswordActual] = useState("");
  const [passwordNova, setPasswordNova] = useState("");
  const [passwordNovaRepetida, setPasswordNovaRepetida] = useState("");
  const [actiu, setActiu] = useState(1);
  const [rolId, setRolId] = useState("");
  const [superFlag, setSuperFlag] = useState(0);
  const [empresaId, setEmpresaId] = useState(null);
  const [rols, setRols] = useState([]);
  const [empreses, setEmpreses] = useState([]);
  const [menuAccions, setMenuAccions] = useState(false);
  const [mostraAjuda, setMostraAjuda] = useState(false);

  const llista = Array.isArray(usuaris) ? usuaris : [];
  const indexActual = usuari ? llista.findIndex((u) => Number(u.idUsuaris) === Number(usuari.idUsuaris)) : -1;
  const potNavegar = ["consultar", "modificar"].includes(mode) && llista.length > 1 && indexActual >= 0;

  const anarAnterior = () => {
    if (!potNavegar) return;
    const nouIndex = indexActual === 0 ? llista.length - 1 : indexActual - 1;
    onNavigate?.(llista[nouIndex]);
  };

  const anarSeguent = () => {
    if (!potNavegar) return;
    const nouIndex = indexActual === llista.length - 1 ? 0 : indexActual + 1;
    onNavigate?.(llista[nouIndex]);
  };

  const resetForm = useCallback(() => {
    setNomUsuari("");
    setPassword("");
    setPasswordReset("");
    setPasswordActual("");
    setPasswordNova("");
    setPasswordNovaRepetida("");
    setActiu(1);
    setRolId("");
    setSuperFlag(0);
    setEmpresaId(empresaSeleccionada?.IdEmpresa ?? null);
  }, [empresaSeleccionada]);

  useEffect(() => {
    const fetchRols = async () => {
      try {
        const res = await fetch(`${API_URL}/Rols.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();
        setRols(data.success ? data.rols || [] : []);
      } catch (e) {
        console.error("Error carregant Rols:", e);
        setRols([]);
      }
    };
    fetchRols();
  }, []);

  useEffect(() => {
    if (!esSuperUsuari) {
      setEmpreses([]);
      return;
    }
    const fetchEmp = async () => {
      try {
        const res = await fetch(`${API_URL}/Empreses.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();
        setEmpreses(data.success ? data.empreses || [] : []);
      } catch (e) {
        console.error("Error carregant Empreses:", e);
        setEmpreses([]);
      }
    };
    fetchEmp();
  }, [esSuperUsuari]);

  useEffect(() => {
    if (usuari && mode !== "nou") {
      setNomUsuari(usuari.NomUsuari || "");
      setPassword("");
      setPasswordReset("");
      setPasswordActual("");
      setPasswordNova("");
      setPasswordNovaRepetida("");
      setActiu(usuari.Actiu ?? 1);
      setRolId(usuari.Rol_idRol ?? "");
      setSuperFlag(usuari.SuperUsuari ? 1 : 0);
      setEmpresaId(esSuperUsuari ? usuari.Empresa_idEmpresa ?? null : empresaSeleccionada?.IdEmpresa ?? null);
    } else if (mode === "nou") {
      resetForm();
    }
    setMenuAccions(false);
  }, [usuari, mode, esSuperUsuari, empresaSeleccionada, resetForm]);

  const rolById = useMemo(() => {
    const m = {};
    for (const r of rols) m[r.idRol] = r;
    return m;
  }, [rols]);

  const rolsAssignables = useMemo(
    () => getRolsAssignables(rolUsuari, esSuperUsuari, rols),
    [rolUsuari, esSuperUsuari, rols]
  );

  useEffect(() => {
    if (isReadOnly || esAutoEdicio) return;
    if (!rolId) return;
    if (!rolsAssignables.some((r) => String(r.idRol) === String(rolId))) {
      setRolId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolsAssignables]);

  const nomRol = (() => {
    const r = rolById[rolId];
    return r ? `${r.Rol}${r.NivellPermisos != null ? ` (${r.NivellPermisos})` : ""}` : "—";
  })();

  const nomEmpresa = (() => {
    if (esSuperUsuari) {
      const em = empreses.find((e) => Number(e.idEmpresa) === Number(empresaId));
      if (em) return em.NomEmpresa ?? em.NomComercial ?? em.Nom ?? `Empresa #${em.idEmpresa}`;
    }
    if (empresaSeleccionada?.NomEmpresa) {
      return `${empresaSeleccionada.NomEmpresa} (${empresaSeleccionada.IdEmpresa})`;
    }
    return empresaId ?? empresaSeleccionada?.IdEmpresa ?? "—";
  })();

  const validar = () => {
    const errs = [];
    if (!nomUsuari.trim()) errs.push("El camp 'Nom d'usuari' és obligatori.");
    if (mode === "nou") {
      if (!password.trim()) errs.push("Has d'indicar un 'Password' per donar d'alta.");
      if (!rolId) errs.push("Has de seleccionar un 'Rol'.");
    }
    if (mode === "modificar" && !esAutoEdicio) {
      if (!rolId) errs.push("Has de seleccionar un 'Rol'.");
    }
    if (!esSuperUsuari && superFlag) errs.push("No tens permisos per crear/modificar un superusuari.");
    if (superFlag && rolById[rolId] && Number(rolById[rolId].NivellPermisos) !== 4) {
      errs.push("Un superusuari ha de tenir Rol d'Administrador.");
    }
    if (!esSuperUsuari && !empresaSeleccionada?.IdEmpresa) errs.push("Falta l'empresa actual.");
    if (mode === "modificar" && esAutoEdicio) {
      const algunOmplert = passwordActual.trim() || passwordNova.trim() || passwordNovaRepetida.trim();
      if (algunOmplert) {
        if (!passwordActual.trim()) errs.push("Indica la teva contrasenya actual.");
        if (!passwordNova.trim()) errs.push("Indica la nova contrasenya.");
        if (!passwordNovaRepetida.trim()) errs.push("Repeteix la nova contrasenya per confirmar-la.");
        if (passwordNova && passwordNovaRepetida && passwordNova !== passwordNovaRepetida) {
          errs.push("La nova contrasenya i la seva repetició no coincideixen.");
        }
      }
    }
    return errs;
  };

  const handleSubmit = async () => {
    if (mode !== "esborrar") {
      const errs = validar();
      if (errs.length > 0) {
        alert("Errors:\n" + errs.join("\n"));
        return;
      }
    }

    const empresaToSend = esSuperUsuari ? empresaId || null : empresaSeleccionada?.IdEmpresa ?? null;

    try {
      if (mode === "nou") {
        const payload = {
          NomUsuari: nomUsuari.trim(),
          Password: password,
          Rol_idRol: Number(rolId),
          Empresa_idEmpresa: empresaToSend,
          SuperUsuari: esSuperUsuari ? Number(!!superFlag) : 0,
        };
        const res = await fetchAutenticat(`${API_URL}/Nouusuari.php`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.success) {
          onSave?.({ ...payload, idUsuaris: data.idUsuaris, Actiu: 1 });
        } else {
          alert("Error en crear l'usuari: " + (data.message || "Desconegut"));
        }
      } else if (mode === "modificar") {
        const payload = {
          idUsuaris: usuari?.idUsuaris,
          NomUsuari: nomUsuari.trim(),
        };
        if (esAutoEdicio) {
          payload.PasswordActual = passwordActual;
          payload.PasswordNova = passwordNova;
          payload.PasswordNovaRepetida = passwordNovaRepetida;
        } else {
          payload.Rol_idRol = Number(rolId);
          payload.SuperUsuari = esSuperUsuari ? Number(!!superFlag) : 0;
          payload.Empresa_idEmpresa = empresaToSend;
          payload.Actiu = Number(!!actiu);
          payload.Password = passwordReset;
        }
        const res = await fetchAutenticat(`${API_URL}/Modificarusuari.php`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.success) {
          onSave?.({ ...usuari, ...payload });
        } else {
          alert("Error en modificar l'usuari: " + (data.message || "Desconegut"));
        }
      } else if (mode === "esborrar") {
        const res = await fetchAutenticat(`${API_URL}/Esborrarusuari.php`, {
          method: "POST",
          body: JSON.stringify({ idUsuaris: usuari.idUsuaris }),
        });
        const data = await res.json();
        if (data.success) {
          onDeleted?.();
        } else {
          alert("Error en esborrar l'usuari: " + (data.message || "Desconegut"));
        }
      }
    } catch (e) {
      console.error("Error en handleSubmit Usuari:", e);
      alert("S'ha produït un error en el procés.");
    }
  };

  const toggle = (checked, onChange, disabled = false) => (
    <span className="dv2-toggleRow">
      <span className="dv2-toggle">
        <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <span className="dv2-toggle-track" />
      </span>
    </span>
  );

  const camp = (label, required, content, mida = "mig") => {
    const extra =
      mida === "ample" ? "dv2-field--ample" : mida === "llarg" ? "dv2-field--llarg" : mida === "curt" ? "dv2-field--curt" : "dv2-field--mig";
    return (
      <label className={`dv2-field ${extra}`}>
        <span className={`dv2-label ${required && !isReadOnly ? "dv2-required" : ""}`}>{label}</span>
        {content}
      </label>
    );
  };

  const titol =
    mode === "nou"
      ? "Nou usuari"
      : mode === "esborrar"
      ? "Esborrar usuari"
      : mode === "modificar"
      ? "Modificar usuari"
      : "Usuaris · Fitxa";

  return (
    <form className="dv2-entity" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
      {mostraAjuda && <AjudaMestres pestanya="usuaris" onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${mode === "esborrar" ? "is-danger" : ""}`} aria-hidden="true">
            {mode === "esborrar" ? "🗑️" : "👤"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titol}</h1>
            <p className="dv2-pageHeader-subtitle">
              {mode === "nou"
                ? "Crea l'accés a l'ERP."
                : mode === "esborrar"
                ? "Aquesta acció no es pot desfer."
                : nomUsuari || "Dades de l'usuari"}
            </p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <BotoAjuda title="Com funcionen els Usuaris" onClick={() => setMostraAjuda(true)} />
          <button type="button" className="dv2-btn" onClick={onCancel}>
            Tornar
          </button>
          {mode === "consultar" && tincPermis(rolUsuari, "MODIFICAR", "USUARIS") && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => onCanviarMode?.("modificar")}>
              Modificar
            </button>
          )}
          {mode === "consultar" && tincPermis(rolUsuari, "ESBORRAR", "USUARIS") && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  <button
                    type="button"
                    className="dv2-menu-item is-danger"
                    onClick={() => {
                      setMenuAccions(false);
                      onCanviarMode?.("esborrar");
                    }}
                  >
                    Esborrar
                  </button>
                </div>
              )}
            </div>
          )}
          {(mode === "nou" || mode === "modificar") && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
              Desar
            </button>
          )}
          {mode === "esborrar" && (
            <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
              Esborrar usuari
            </button>
          )}
        </div>
      </div>

      {mode === "esborrar" && (
        <div className="dv2-banner dv2-banner--danger">
          Aquesta acció no es pot desfer. Si l'usuari té historial, el servidor pot no deixar-lo esborrar.
        </div>
      )}

      <div className="dv2-entity-body">
        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">1</span>
            <h2 className="dv2-card-title">Identificació</h2>
          </div>
          <div className="dv2-fields dv2-fields--sizes">
            {camp(
              "Nom d'usuari",
              true,
              isReadOnly ? (
                <span className="dv2-value">{nomUsuari || "—"}</span>
              ) : (
                <input
                  type="text"
                  name="nomUsuariNouRegistre"
                  className="dv2-input"
                  value={nomUsuari}
                  autoComplete="off"
                  autoFocus
                  onChange={(e) => setNomUsuari(e.target.value)}
                />
              ),
              "llarg"
            )}
            {mode === "nou" &&
              camp(
                "Password",
                true,
                <input
                  type="password"
                  name="passwordNouRegistre"
                  className="dv2-input"
                  value={password}
                  autoComplete="new-password"
                  onChange={(e) => setPassword(e.target.value)}
                />,
                "llarg"
              )}
            {mode === "modificar" &&
              !esAutoEdicio &&
              camp(
                "Nova contrasenya",
                false,
                <input
                  type="password"
                  className="dv2-input"
                  value={passwordReset}
                  autoComplete="new-password"
                  placeholder="Deixar buit = sense canvi"
                  onChange={(e) => setPasswordReset(e.target.value)}
                />,
                "llarg"
              )}
          </div>
        </section>

        {mode === "modificar" && esAutoEdicio && (
          <section className="dv2-card">
            <div className="dv2-card-head">
              <span className="dv2-card-num">2</span>
              <h2 className="dv2-card-title">Canvi de contrasenya</h2>
            </div>
            <p className="dv2-hint">
              Ets tu mateix. Només pots canviar la teva contrasenya des d'aquí (has de saber l'actual). Si l'has perduda, demana a un altre administrador que te la reiniciï.
            </p>
            <div className="dv2-fields dv2-fields--sizes">
              {camp(
                "Contrasenya actual",
                false,
                <input type="password" className="dv2-input" value={passwordActual} autoComplete="current-password" onChange={(e) => setPasswordActual(e.target.value)} />,
                "llarg"
              )}
              {camp(
                "Nova contrasenya",
                false,
                <input type="password" className="dv2-input" value={passwordNova} autoComplete="new-password" onChange={(e) => setPasswordNova(e.target.value)} />,
                "llarg"
              )}
              {camp(
                "Repeteix la nova contrasenya",
                false,
                <input type="password" className="dv2-input" value={passwordNovaRepetida} autoComplete="new-password" onChange={(e) => setPasswordNovaRepetida(e.target.value)} />,
                "llarg"
              )}
            </div>
          </section>
        )}

        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">{mode === "modificar" && esAutoEdicio ? "3" : "2"}</span>
            <h2 className="dv2-card-title">Accés</h2>
          </div>
          <div className="dv2-fields dv2-fields--sizes">
            {camp(
              "Rol",
              true,
              isReadOnly || esAutoEdicio ? (
                <span className="dv2-value">{nomRol}</span>
              ) : (
                <select className="dv2-select" value={rolId} onChange={(e) => setRolId(e.target.value)}>
                  <option value="">Selecciona rol</option>
                  {rolsAssignables.map((r) => (
                    <option key={r.idRol} value={r.idRol}>
                      {r.Rol}
                      {r.NivellPermisos != null ? ` (${r.NivellPermisos})` : ""}
                    </option>
                  ))}
                </select>
              ),
              "llarg"
            )}
            {mode !== "nou" &&
              !esAutoEdicio &&
              camp(
                "Actiu",
                false,
                isReadOnly ? <span className="dv2-value">{actiu ? "Sí" : "No"}</span> : toggle(actiu, (v) => setActiu(v ? 1 : 0)),
                "curt"
              )}
            {!esAutoEdicio &&
              camp(
                "Superusuari",
                false,
                isReadOnly ? (
                  <span className="dv2-value">{superFlag ? "Sí" : "No"}</span>
                ) : (
                  toggle(superFlag, (v) => setSuperFlag(v ? 1 : 0), !esSuperUsuari)
                ),
                "curt"
              )}
            {camp(
              "Empresa",
              false,
              isReadOnly || esAutoEdicio || !esSuperUsuari ? (
                <span className="dv2-value">{nomEmpresa}</span>
              ) : (
                <select
                  className="dv2-select"
                  value={empresaId ?? ""}
                  onChange={(e) => setEmpresaId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Selecciona empresa</option>
                  {empreses.map((em) => (
                    <option key={em.idEmpresa} value={em.idEmpresa}>
                      {em.NomEmpresa ?? `Empresa #${em.idEmpresa}`}
                    </option>
                  ))}
                </select>
              ),
              "llarg"
            )}
          </div>
        </section>
      </div>

      {(potNavegar || mode === "nou" || mode === "modificar" || mode === "esborrar") && (
        <div className="dv2-entity-dock">
          {potNavegar && (
            <div className="dv2-recordNav">
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={anarAnterior} title="Anterior">
                ‹
              </button>
              <span className="dv2-recordNav-info">
                {indexActual + 1} / {llista.length}
              </span>
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={anarSeguent} title="Següent">
                ›
              </button>
            </div>
          )}
          {(mode === "nou" || mode === "modificar" || mode === "esborrar") && (
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" onClick={onCancel}>
                Cancel·lar
              </button>
              {mode === "esborrar" ? (
                <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
                  Esborrar usuari
                </button>
              ) : (
                <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
                  {mode === "nou" ? "Desar usuari" : "Desar"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </form>
  );
}

export default Detallsusuari;
